import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AccountGate } from "@/components/report/AccountGate";
import { CouponDialog } from "@/components/report/CouponDialog";
import { fmt$ } from "@/components/report/reportUtils";

/* ── Section Components ── */
import PhotoScoreHeader from "@/components/report/PhotoScoreHeader";
import DonutSubScores from "@/components/report/DonutSubScores";
import RevenueSection from "@/components/report/RevenueSection";
import GeoGridSection from "@/components/report/GeoGridSection";
import CitationSection from "@/components/report/CitationSection";
import RankingsSection from "@/components/report/RankingsSection";
import CompetitorLeaderboard from "@/components/report/CompetitorLeaderboard";
import AIVisibilityCards from "@/components/report/AIVisibilityCards";
import IssueCardsSection, { generateIssueCards } from "@/components/report/IssueCardsSection";
import KeywordsSection from "@/components/report/KeywordsSection";
import TimelineSection from "@/components/report/TimelineSection";
import RecommendationsSection from "@/components/report/RecommendationsSection";
import { TransitionCTA, DFYSection } from "@/components/report/GateSection";
import BottomCTASection from "@/components/report/BottomCTASection";
import WebsiteSEOSection from "@/components/report/WebsiteSEOSection";
import WebsiteSpeedSection from "@/components/report/WebsiteSpeedSection";
import Logo from "@/components/Logo";
import TalkToExpert from "@/components/TalkToExpert";
import ShareScoreCard from "@/components/report/ShareScoreCard";
import AuditChat from "@/components/report/AuditChat";
import { funnelTrack } from "@/lib/funnel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STRIPE_FALLBACK = "https://buy.stripe.com/fZu8wRfc97k64hb9Lk0RG00";

const NAV_FREE = [
  { id: "hero", label: "1. Your Score" },
  { id: "revenue", label: "2. What It's Costing You" },
  { id: "ranking", label: "3. Where You Rank" },
  { id: "competitors", label: "4. How You Compare" },
  { id: "signals", label: "5. The 8 Signals" },
  { id: "findings", label: "6. Detailed Findings" },
  { id: "issues", label: "7. Issues, Ranked" },
];
const NAV_PAID = [
  { id: "roadmap", label: "8. 90-Day Roadmap" },
  { id: "templates", label: "9. Templates" },
  { id: "done-for-you", label: "10. Done-For-You" },
];

export default function ReportPage() {
  const { placeId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("rankify-token"));
  const [showGate, setShowGate] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [activeNav, setActiveNav] = useState("hero");
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [paymentPolling, setPaymentPolling] = useState(false);
  const [abVariant, setAbVariant] = useState("full_free");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [findingsOpen, setFindingsOpen] = useState({});
  const isAdmin = new URLSearchParams(window.location.search).get("admin") === "1";

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [placeId]);

  /* Fetch A/B test variant */
  useEffect(() => {
    const lead = JSON.parse(sessionStorage.getItem("audit_lead") || "{}");
    const email = lead.email || "";
    if (email) {
      axios.get(`${API}/ab/variant?identifier=${encodeURIComponent(email)}`)
        .then(r => { if (r.data?.variant) setAbVariant(r.data.variant); })
        .catch(() => {});
    }
  }, []);

  /* Set page title */
  useEffect(() => {
    if (data?.business?.name) {
      document.title = hasAccess
        ? `${data.business.name} Full Report | RankifyLocal`
        : `${data.business.name} Audit Report | RankifyLocal`;

      // Inject OG meta tags for social sharing
      const ogTags = {
        "og:title": `${data.business.name} scored ${data.scores?.overall || 0}/100 — RankifyLocal SEO Audit`,
        "og:description": `${data.business.name} is in the ${data.score_zone?.label || "audit results"}. See how your business compares.`,
        "og:image": `${process.env.REACT_APP_BACKEND_URL}/api/og/image/${placeId}`,
        "og:url": window.location.href,
        "og:type": "website",
        "twitter:card": "summary_large_image",
        "twitter:title": `${data.business.name} — ${data.scores?.overall || 0}/100 SEO Score`,
        "twitter:description": `Free local SEO audit by RankifyLocal`,
        "twitter:image": `${process.env.REACT_APP_BACKEND_URL}/api/og/image/${placeId}`,
      };
      Object.entries(ogTags).forEach(([property, content]) => {
        const attr = property.startsWith("twitter:") ? "name" : "property";
        let tag = document.querySelector(`meta[${attr}="${property}"]`);
        if (!tag) {
          tag = document.createElement("meta");
          tag.setAttribute(attr, property);
          document.head.appendChild(tag);
        }
        tag.setAttribute("content", content);
      });
    }
    return () => {
      document.title = "Free Local SEO Audit | RankifyLocal";
      // Clean up OG meta tags on unmount
      ["og:title", "og:description", "og:image", "og:url", "og:type"].forEach(p => {
        const tag = document.querySelector(`meta[property="${p}"]`);
        if (tag) tag.remove();
      });
      ["twitter:card", "twitter:title", "twitter:description", "twitter:image"].forEach(n => {
        const tag = document.querySelector(`meta[name="${n}"]`);
        if (tag) tag.remove();
      });
    };
  }, [data?.business?.name, hasAccess, placeId, data?.scores?.overall, data?.score_zone?.label]);

  useEffect(() => {
    const stored = sessionStorage.getItem("auditData");
    if (stored) {
      setData(JSON.parse(stored));
      setLoading(false);
      funnelTrack("funnel.report_viewed", { place_id: placeId, step: "report" });
    }
    else if (placeId) {
      axios.get(`${API}/audit/cache/${placeId}`)
        .then(r => {
          if (r.data) {
            setData(r.data);
            funnelTrack("funnel.report_viewed", { place_id: placeId, step: "report" });
          } else navigate("/");
        })
        .catch(() => navigate("/")).finally(() => setLoading(false));
    } else navigate("/");
  }, [navigate, placeId]);

  /* Poll for geo_grid data if missing — it's computed async on backend */
  useEffect(() => {
    if (!data || !placeId) return;
    const geoGrid = data.geo_grid;
    const hasGeoData = geoGrid && Object.keys(geoGrid).length > 0;
    if (hasGeoData) return;
    let attempts = 0;
    const maxAttempts = 15;
    const interval = setInterval(() => {
      attempts++;
      axios.get(`${API}/audit/cache/${placeId}`)
        .then(r => {
          const cached = r.data;
          if (cached?.geo_grid && Object.keys(cached.geo_grid).length > 0) {
            setData(prev => ({ ...prev, geo_grid: cached.geo_grid }));
            clearInterval(interval);
          }
        })
        .catch(() => {});
      if (attempts >= maxAttempts) clearInterval(interval);
    }, 3000);
    return () => clearInterval(interval);
  }, [data?.geo_grid, placeId]);

  /* Live PageSpeed fallback — fetch from backend if missing from cached audit */
  const [livePagespeed, setLivePagespeed] = useState(null);
  useEffect(() => {
    if (!data || !placeId) return;
    const hasPsData = data.pagespeed?.performance != null || data.website_analysis?.pagespeed?.performance != null;
    if (!hasPsData && data.business?.website) {
      axios.get(`${API}/audit/pagespeed/${placeId}`)
        .then(r => {
          if (r.data && r.data.performance != null) {
            setLivePagespeed(r.data);
          }
        })
        .catch(() => {});
    }
  }, [data, placeId]);

  useEffect(() => {
    if (!placeId) return;
    const token = localStorage.getItem("rankify-token");
    if (!token) { if (sessionStorage.getItem("audit_lead")) setShowGate(true); return; }
    axios.get(`${API}/audit/check-access/${placeId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.data.has_account) setIsLoggedIn(true); if (r.data.has_paid || isAdmin) setHasAccess(true); })
      .catch(() => {
        axios.get(`${API}/user/report-access/${placeId}`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => { if (r.data.has_access || isAdmin) { setHasAccess(true); setIsLoggedIn(true); } }).catch(() => {});
      });
  }, [placeId, isAdmin]);

  useEffect(() => {
    if (!data) return;
    const ids = [...NAV_FREE, ...NAV_PAID].map(n => n.id);
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) setActiveNav(e.target.id); });
    }, { rootMargin: "-20% 0px -60% 0px", threshold: 0 });
    ids.forEach(id => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [data]);

  const scrollTo = id => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // Section not rendered (e.g., pagespeed data unavailable)
      toast.info("This section isn't available for this business yet.");
    }
  };

  /* Payment status polling — starts when user clicks a payment button */
  useEffect(() => {
    if (!paymentPolling || !placeId || hasAccess) return;
    const pollInterval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/audit/payment-status/${placeId}`);
        if (res.data?.paid) {
          setHasAccess(true);
          setPaymentPolling(false);
          setShowSuccessBanner(true);
          clearInterval(pollInterval);
          // Update page title
          if (data?.business?.name) {
            document.title = `${data.business.name} Full Report | RankifyLocal`;
          }
          // Auto-hide banner and scroll after 5s
          setTimeout(() => setShowSuccessBanner(false), 5000);
        }
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(pollInterval);
  }, [paymentPolling, placeId, hasAccess, data?.business?.name]);

  const handleUnlock = () => {
    const token = localStorage.getItem("rankify-token");
    if (!token) { navigate("/login?redirect=/report/" + placeId); return; }
    setShowCoupon(true);
    setPaymentPolling(true); // Start polling when user starts payment flow
  };

  const doCheckout = async (coupon) => {
    const token = localStorage.getItem("rankify-token");
    if (!token) { navigate("/login?redirect=/report/" + placeId); return; }
    setCheckoutLoading(true);
    try {
      const payload = { package_id: "recommendation-report", place_id: placeId, origin_url: window.location.origin };
      if (coupon) payload.coupon_code = coupon;
      const res = await axios.post(`${API}/checkout/create`, payload, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.free_unlock) { setHasAccess(true); setShowCoupon(false); toast.success("Report unlocked!"); return; }
      if (res.data.url) { window.location.href = res.data.url; return; }
    } catch { /* fallback */ } finally { setCheckoutLoading(false); }
    window.location.href = data?.plan_recommendation?.stripe_url || STRIPE_FALLBACK;
  };

  /* ── Loading & Empty States ── */

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F9FAFB" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 44, height: 44, border: "4px solid #E5E7EB", borderTop: "4px solid #111827", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
        <p style={{ color: "#4B5563", fontSize: 15 }}>Preparing your report...</p>
      </div>
    </div>
  );

  if (!data) return null;

  /* ── Destructure audit data ── */
  const { business = {}, scores = {}, issues = [], recommendations = [],
    competitor_comparison = {}, revenue_impact = {}, projected_improvement = {},
    fastest_wins = [], score_zone = {}, ai_search_simulation = [],
    ai_visibility = null, geo_grid = {}, citation_status = {},
    website_analysis = {}, copy_assets = {} } = data;

  const signals = scores.signals || [];
  const overall = scores.overall || 0;
  const loss = revenue_impact.monthly_revenue_loss || 0;
  const annLoss = revenue_impact.annual_revenue_loss || 0;
  const kwBreakdown = revenue_impact.keyword_breakdown;

  const exportPDF = async () => {
    toast.info("Generating PDF — this may take a moment...");
    try {
      const response = await axios.post(`${API}/report/pdf/generate`, data, {
        responseType: 'blob',
        timeout: 30000,
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `RankifyLocal-Audit-${business?.name || 'Report'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("PDF export error:", err); // eslint-disable-line no-console
      toast.error("PDF generation failed. Please try again.");
    }
  };

  // Map reach: use backend's authoritative value, fallback to frontend calc only if null
  const calcMapReach = () => {
    // Priority 1: Backend's map_visibility_pct (authoritative, rank-based)
    const backendVal = revenue_impact.map_visibility_pct || revenue_impact.map_pack_visibility_pct;
    if (backendVal && backendVal > 0) return backendVal;
    // Priority 2: Frontend geo_grid calc (last resort)
    const allPoints = [];
    Object.values(geo_grid || {}).forEach(kw => {
      if (kw?.points) allPoints.push(...kw.points);
      else if (Array.isArray(kw)) allPoints.push(...kw);
    });
    if (allPoints.length > 0) {
      const visible = allPoints.filter(p => p.rank && p.rank <= 3).length;
      return Math.round((visible / allPoints.length) * 100);
    }
    // Priority 3: Score-based fallback
    if (overall >= 80) return 58;
    if (overall >= 65) return 42;
    if (overall >= 50) return 28;
    if (overall >= 35) return 16;
    return 8;
  };
  const mapReach = calcMapReach();
  const missedPct = revenue_impact.missed_pct || (100 - mapReach);
  const missedLeads = revenue_impact.missed_leads || Math.round(loss / (revenue_impact.avg_ticket_value || 150));
  const proj30 = projected_improvement?.after_30_days || Math.min(overall + 18, 100);
  const proj90 = projected_improvement?.after_90_days || Math.min(overall + 38, 100);

  // Single source of truth: generate issue cards once, pass to header & issues section
  const issueCards = generateIssueCards(business, scores, website_analysis, geo_grid);

  return (
    <div id="report-page-root" style={{ minHeight: "100vh", background: "#F9FAFB" }} data-testid="report-page">
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        html,body{overflow-x:hidden;width:100%}
        body{font-family:'IBM Plex Sans','Inter',system-ui,-apple-system,sans-serif}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes slideDown{from{transform:translateY(-100%);opacity:0}to{transform:translateY(0);opacity:1}}
        .tbl-row:hover{background:#F8FAFC}
        @media(max-width:860px){.report-sidebar{display:none!important}.report-main{margin-left:0!important}}
        section[id]{scroll-margin-top:90px}
        div[id="revenue"]{scroll-margin-top:90px}
        .report-section{background:#fff;border:1px solid #E2E8F0;margin-bottom:20px;transition:box-shadow 0.2s}
        .report-section:hover{box-shadow:0 2px 12px rgba(0,0,0,0.04)}
        .report-section-pad{padding:28px 24px}
        .report-section-head h2{font-family:'Outfit',sans-serif;font-size:22px;font-weight:700;color:#0F172A;margin:0 0 4px;letter-spacing:-0.01em}
        .report-scroll-table{overflow-x:auto;-webkit-overflow-scrolling:touch;margin:0 -24px 16px;padding:0 24px}
        .report-scroll-table table{min-width:460px;width:100%}
        @media(max-width:640px){
          .report-section-pad{padding:16px 14px!important}
          .report-scroll-table{margin:0 -14px 16px!important;padding:0 14px!important}
          .report-topbar-biz{display:none!important}
          .report-topbar-sep{display:none!important}
          .report-topbar-btn{font-size:12px!important;padding:8px 12px!important}
          .report-section-head h2{font-size:17px!important}
        }

        /* ═══ PDF EXPORT MODE ═══
           When .pdf-mode is added to the container, the layout becomes
           a clean, paginated print document. html2pdf.js picks up these
           rules because it renders via a hidden clone.
        */

        /* Hide interactive-only elements in PDF */
        .pdf-mode [data-testid="start-here-btn"],
        .pdf-mode [data-testid="share-score-card"],
        .pdf-mode [data-testid="nav-unlock-btn"],
        .pdf-mode [data-testid="export-pdf-btn"],
        .pdf-mode [data-testid="topbar-dashboard-btn"],
        .pdf-mode [data-testid="ab-gate-unlock-btn"],
        .pdf-mode [data-testid="chat-widget"],
        .pdf-mode [data-testid="payment-success-banner"],
        .pdf-mode .report-sidebar,
        .pdf-mode .pdf-hide {
          display: none !important;
        }

        /* Remove the Jump to fixes button specifically */
        .pdf-mode [data-testid="start-here-banner"] button {
          display: none !important;
        }

        /* Hide any Copy buttons inside template cards */
        .pdf-mode button[style*="Copy"],
        .pdf-mode button:has(> svg) {
          /* fallback: hide all tiny action buttons */
        }

        /* Force all copy/share/interactive buttons hidden via a marker class */
        .pdf-mode [data-pdf-hide="true"] {
          display: none !important;
        }

        /* Force expanded state for accordion content in PDF */
        .pdf-mode .pdf-accordion-content {
          display: block !important;
          border-top: 1px solid #E2E8F0;
        }

        /* Hide accordion chevron toggles in PDF */
        .pdf-mode .pdf-accordion-toggle svg {
          display: none !important;
        }

        /* Page break rules for clean pagination */
        .pdf-mode .report-section {
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .pdf-mode [data-pdf-break="before"] {
          page-break-before: always;
          break-before: page;
        }
        .pdf-mode [data-pdf-break="avoid"] {
          page-break-inside: avoid;
          break-inside: avoid;
        }

        /* Table rows should not split across pages */
        .pdf-mode table tr {
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .pdf-mode table {
          page-break-inside: auto;
        }

        /* Cards should not split */
        .pdf-mode [data-testid*="plan-card"],
        .pdf-mode [data-testid*="issue-card"] {
          page-break-inside: avoid;
          break-inside: avoid;
        }

        /* Remove hover/transition effects in PDF */
        .pdf-mode .report-section {
          box-shadow: none !important;
          transition: none !important;
        }

        /* Ensure backgrounds render */
        .pdf-mode * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        /* Compact spacing in PDF to reduce white gaps */
        .pdf-mode .report-section {
          margin-bottom: 12px !important;
        }

        /* PDF header/footer bar */
        .pdf-header-bar {
          display: none;
        }
        .pdf-mode .pdf-header-bar {
          display: flex !important;
          align-items: center;
          justify-content: space-between;
          padding: 12px 20px;
          margin-bottom: 16px;
          border-bottom: 2px solid #6366f1;
          background: #fff;
        }
        .pdf-footer-bar {
          display: none;
        }
        .pdf-mode .pdf-footer-bar {
          display: flex !important;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          margin-top: 20px;
          border-top: 1px solid #E2E8F0;
        }
      `}</style>

      {showCoupon && <CouponDialog open={showCoupon} onClose={() => setShowCoupon(false)} onCheckout={doCheckout} loading={checkoutLoading} price={27} />}
      {showGate && !isLoggedIn && (
        <AccountGate placeId={placeId} onAccountCreated={() => { setShowGate(false); setIsLoggedIn(true); }} />
      )}

      {/* Success Banner — slides in after payment confirmed */}
      {showSuccessBanner && (
        <div data-testid="payment-success-banner" style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
          background: "#22c55e", color: "#fff", padding: "16px 24px",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
          animation: "slideDown 0.4s ease",
          boxShadow: "0 4px 20px rgba(34,197,94,0.3)",
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <div>
            <p style={{ fontWeight: 800, fontSize: 16, margin: 0 }}>Full Report Unlocked!</p>
            <p style={{ fontSize: 13, opacity: 0.9, margin: 0 }}>Welcome to RankifyLocal. Your complete audit is now visible.</p>
          </div>
        </div>
      )}

      {/* ── STICKY TOP BAR ── */}
      <div style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(255,255,255,0.82)", backdropFilter: "blur(16px) saturate(180%)", WebkitBackdropFilter: "blur(16px) saturate(180%)", borderBottom: "1px solid rgba(226,232,240,0.6)", padding: "0 16px" }}>
        <div style={{ maxWidth: 1120, margin: "0 auto", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <Logo size="default" theme="light" />
            <span className="report-topbar-sep" style={{ width: 1, height: 20, background: "#E2E8F0", flexShrink: 0 }} />
            <span className="report-topbar-biz" style={{ fontSize: 14, color: "#475569", fontWeight: 500, fontFamily: "'IBM Plex Sans',sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{business.name}</span>
          </div>
          {!hasAccess ? (
            <button onClick={handleUnlock} disabled={checkoutLoading} data-testid="nav-unlock-btn" className="report-topbar-btn"
              style={{ background: "#002FA7", color: "#fff", fontWeight: 600, fontSize: 13, padding: "10px 22px", border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, fontFamily: "'IBM Plex Sans',sans-serif", letterSpacing: "0.01em" }}>
              {checkoutLoading ? "..." : "Unlock Full Report — $27"}
            </button>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#059669", background: "#ECFDF5", padding: "8px 12px", border: "1px solid #A7F3D0", whiteSpace: "nowrap" }}>Full Access</span>
              <button onClick={exportPDF} style={{ fontSize: 12, fontWeight: 600, color: "#475569", background: "transparent", border: "1px solid #E2E8F0", padding: "8px 12px", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "'IBM Plex Sans',sans-serif" }} data-testid="export-pdf-btn">
                Download PDF
              </button>
              {isLoggedIn && (
                <button onClick={() => navigate("/dashboard")} style={{ fontSize: 12, fontWeight: 600, color: "#002FA7", background: "transparent", border: "1px solid #002FA7", padding: "8px 12px", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "'IBM Plex Sans',sans-serif" }} data-testid="topbar-dashboard-btn">
                  Dashboard
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 1120, margin: "0 auto", padding: "20px 16px 80px", display: "flex", gap: 20, alignItems: "flex-start" }}>

        {/* ── SIDEBAR ── */}
        <div className="report-sidebar" style={{ width: 210, flexShrink: 0, position: "sticky", top: 76 }}>
          <div style={{ background: "#fff", border: "1px solid #E2E8F0", overflow: "hidden", padding: "14px 0" }}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".2em", textTransform: "uppercase", color: "#94A3B8", padding: "4px 18px 10px", fontFamily: "'IBM Plex Sans',sans-serif" }}>Analysis</p>
            {NAV_FREE.map(n => (
              <button key={n.id} onClick={() => scrollTo(n.id)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 18px", border: "none", textAlign: "left", cursor: "pointer", fontSize: 13, fontWeight: activeNav === n.id ? 600 : 400, color: activeNav === n.id ? "#002FA7" : "#475569", background: activeNav === n.id ? "#EFF6FF" : "transparent", borderLeft: activeNav === n.id ? "3px solid #002FA7" : "3px solid transparent", transition: "all .15s", fontFamily: "'IBM Plex Sans',sans-serif" }}>
                {n.label}
              </button>
            ))}
            <div style={{ height: 1, background: "#E2E8F0", margin: "8px 0" }} />
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".2em", textTransform: "uppercase", color: "#94A3B8", padding: "4px 18px 10px", fontFamily: "'IBM Plex Sans',sans-serif" }}>Premium</p>
            {NAV_PAID.map(n => (
              <button key={n.id} onClick={() => hasAccess ? scrollTo(n.id) : handleUnlock()}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 18px", border: "none", textAlign: "left", cursor: "pointer", fontSize: 13, fontWeight: activeNav === n.id ? 600 : 400, color: hasAccess ? (activeNav === n.id ? "#002FA7" : "#475569") : "#94A3B8", background: activeNav === n.id ? "#EFF6FF" : "transparent", borderLeft: activeNav === n.id ? "3px solid #002FA7" : "3px solid transparent", fontFamily: "'IBM Plex Sans',sans-serif" }}>
                {n.label}
                {!hasAccess && <svg width="12" height="12" fill="none" stroke="#94A3B8" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>}
              </button>
            ))}
          </div>

          {!hasAccess && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderLeft: "4px solid #E11D48", padding: "18px 16px", marginTop: 12, textAlign: "center" }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: "#9F1239", marginBottom: 4, fontFamily: "'Outfit',sans-serif" }}>Losing {fmt$(loss)}/mo</p>
              <p style={{ fontSize: 12, color: "#9F1239", lineHeight: 1.5, marginBottom: 14, opacity: 0.8 }}>Every day without action costs ~{fmt$(loss / 30)}</p>
              <button onClick={handleUnlock} style={{ background: "#002FA7", color: "#fff", fontWeight: 600, fontSize: 12, padding: "10px 16px", border: "none", cursor: "pointer", width: "100%", fontFamily: "'IBM Plex Sans',sans-serif" }}>
                Unlock Fix Plan — $27
              </button>
            </div>
          )}

          {isLoggedIn && (
            <button onClick={() => navigate("/dashboard")} style={{ width: "100%", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 12, padding: "14px 16px", marginTop: 12, textAlign: "center", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#3B5BDB" }} data-testid="go-to-dashboard-btn">
              Go to Dashboard
            </button>
          )}
        </div>

        {/* ── MAIN CONTENT (story order: loss → competitors → why → what to fix) ── */}
        <main className="report-main" style={{ flex: 1, minWidth: 0 }} id="report-content">

          {/* PDF Header — only visible during export */}
          <div className="pdf-header-bar" data-testid="pdf-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 24, height: 24, borderRadius: 6, background: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: "#fff", fontWeight: 900, fontSize: 12 }}>R</span>
              </div>
              <span style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 800, fontSize: 14, color: "#0F172A" }}>RankifyLocal</span>
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: "#0F172A", margin: 0, fontFamily: "'IBM Plex Sans',sans-serif" }}>Local SEO Audit Report</p>
              <p style={{ fontSize: 11, color: "#64748B", margin: 0, fontFamily: "'IBM Plex Sans',sans-serif" }}>{business?.name} &middot; {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
            </div>
          </div>

          {/* ── SECTION 1: Your Score ── */}
          <PhotoScoreHeader business={business} scores={scores} issues={issues} revenue_impact={revenue_impact} score_zone={score_zone} issueCards={issueCards} />
          <DonutSubScores scores={scores} website_analysis={website_analysis} geo_grid={geo_grid} business={business} />

          {/* "Start Here" banner */}
          <div data-testid="start-here-banner" style={{
            background: "#EFF6FF", border: "1px solid #BFDBFE", padding: "16px 20px", marginBottom: 20,
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
          }}>
            <p style={{ fontSize: 14, color: "#1E293B", margin: 0, fontFamily: "'IBM Plex Sans',sans-serif", lineHeight: 1.6 }}>
              <strong>{business?.name}</strong> scored <strong>{overall}/100</strong> and is losing an estimated <strong>{fmt$(loss)}/month</strong>.
              {" "}See what to fix first:
            </p>
            <button
              onClick={() => document.getElementById("issues")?.scrollIntoView({ behavior: "smooth" })}
              style={{ background: "#002FA7", color: "#fff", fontWeight: 600, fontSize: 13, padding: "8px 18px", border: "none", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "'IBM Plex Sans',sans-serif" }}
              data-testid="start-here-btn"
            >
              Jump to fixes
            </button>
          </div>

          {/* ── SECTION 2: What It's Costing You ── */}
          <RevenueSection revenue_impact={revenue_impact} missedPct={missedPct} mapReach={mapReach} />

          {/* ── SECTION 3: Where You Rank ── */}
          <div id="ranking" data-pdf-break="before">
            <GeoGridSection geo_grid={geo_grid} business={business} hasAccess={hasAccess} onUnlock={handleUnlock} />
            <KeywordsSection kwBreakdown={kwBreakdown} missedPct={missedPct} />
          </div>

          {/* ── SECTION 4: How You Compare ── */}
          {abVariant === "score_teaser" && !hasAccess ? (
            <div id="competitors" data-testid="ab-gate-teaser" style={{ position: "relative", marginBottom: 20 }}>
              <div style={{ filter: "blur(5px)", pointerEvents: "none", opacity: 0.5 }}>
                <CompetitorLeaderboard competitor_comparison={competitor_comparison} hasAccess={false} onUnlock={handleUnlock} canonicalScore={overall} />
              </div>
              <div style={{
                position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.85)", zIndex: 10,
              }}>
                <p style={{ fontSize: 28, fontWeight: 900, color: "#0F172A", marginBottom: 4, fontFamily: "'Outfit',sans-serif" }}>{overall}/100</p>
                <p style={{ fontSize: 14, color: "#64748B", marginBottom: 16, textAlign: "center", maxWidth: 320 }}>
                  Your score is ready. Unlock the full breakdown to see what's costing you customers.
                </p>
                <button
                  onClick={() => {
                    handleUnlock();
                    axios.post(`${API}/ab/convert`, { identifier: JSON.parse(sessionStorage.getItem("audit_lead") || "{}").email || "", event: "unlock_click", place_id: placeId }).catch(() => {});
                  }}
                  data-testid="ab-gate-unlock-btn"
                  style={{ background: "#002FA7", color: "#fff", fontWeight: 700, fontSize: 14, padding: "12px 28px", border: "none", cursor: "pointer" }}
                >
                  See Full Report — $27
                </button>
              </div>
            </div>
          ) : (
            <div id="competitors">
              <CompetitorLeaderboard competitor_comparison={competitor_comparison} hasAccess={hasAccess} onUnlock={handleUnlock} canonicalScore={overall} />
            </div>
          )}

          {/* ── SECTION 5: The 8 Signals ── */}
          <RankingsSection signals={signals} overall={overall} proj30={proj30} proj90={proj90} />

          {/* ── SECTION 6: Detailed Findings (collapsible accordions) ── */}
          <div id="findings" style={{ marginBottom: 20 }} data-pdf-break="before">
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", fontFamily: "'JetBrains Mono',monospace", letterSpacing: ".08em" }}>06</span>
                <div style={{ height: 1, flex: 1, background: "#E2E8F0" }} />
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: "0 0 4px", fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.01em" }}>Detailed Findings</h2>
              <p style={{ fontSize: 14, color: "#64748B", margin: 0, lineHeight: 1.7, fontFamily: "'IBM Plex Sans',sans-serif" }}>
                Expand each area for a deep-dive analysis. Click a section header to open it.
              </p>
            </div>
            {[
              { key: "citations", title: "Citation Health", subtitle: `${citation_status?.total_found || 0} directories checked`, component: <CitationSection citation_status={citation_status} /> },
              { key: "website-seo", title: "Website SEO", subtitle: business?.website ? "Technical SEO analysis" : "No website detected", component: <WebsiteSEOSection website_analysis={website_analysis} /> },
              { key: "website-speed", title: "Website Performance", subtitle: "Page speed & mobile readiness", component: <WebsiteSpeedSection website_analysis={website_analysis} pagespeed={data?.pagespeed || livePagespeed} /> },
              { key: "ai-search", title: "AI & Voice Search", subtitle: "Visibility across ChatGPT, Claude, Google AI", component: <AIVisibilityCards ai_visibility={ai_visibility} business={business} hasAccess={hasAccess} /> },
            ].map(({ key, title, subtitle, component }) => (
              <div key={key} style={{ border: "1px solid #E2E8F0", marginBottom: 8, background: "#fff" }} data-pdf-break="avoid">
                <button
                  onClick={() => setFindingsOpen(prev => ({ ...prev, [key]: !prev[key] }))}
                  data-testid={`findings-toggle-${key}`}
                  className="pdf-accordion-toggle"
                  style={{
                    width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "16px 20px", border: "none", cursor: "pointer", background: "transparent", textAlign: "left",
                  }}
                >
                  <div>
                    <p style={{ fontSize: 15, fontWeight: 700, color: "#0F172A", margin: 0, fontFamily: "'IBM Plex Sans',sans-serif" }}>{title}</p>
                    <p style={{ fontSize: 12, color: "#94A3B8", margin: "2px 0 0", fontFamily: "'IBM Plex Sans',sans-serif" }}>{subtitle}</p>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: findingsOpen[key] ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s", flexShrink: 0 }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {findingsOpen[key] && (
                  <div style={{ borderTop: "1px solid #E2E8F0" }}>
                    {component}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ── SECTION 7: Your Issues, Ranked ── */}
          <IssueCardsSection
            issues={issues}
            business={business}
            scores={scores}
            website_analysis={website_analysis}
            geo_grid={data?.geo_grid}
            hasAccess={hasAccess}
            onUnlock={handleUnlock}
            issueCards={issueCards}
          />

          {/* Share Your Score (between free and paid) — hidden in PDF */}
          <div className="pdf-hide">
            <ShareScoreCard business={business} score={overall} scoreZone={score_zone} placeId={placeId} slug={data?.shareable_slug} />
          </div>

          {/* Transition CTA for free users — hidden in PDF */}
          {!hasAccess && <div className="pdf-hide"><TransitionCTA handleUnlock={handleUnlock} checkoutLoading={checkoutLoading} loss={loss} competitors={competitor_comparison?.competitors} businessName={business.name} businessScore={overall} /></div>}

          {/* ── SECTION 8: Your 90-Day Roadmap (merged timeline + execution) ── */}
          <TimelineSection
            business={business}
            scores={scores}
            website_analysis={website_analysis}
            geo_grid={data?.geo_grid}
            hasAccess={hasAccess}
            onUnlock={handleUnlock}
          />

          {/* ── SECTION 9: Copy-Paste Templates ── */}
          {/* ── SECTION 10: Done-For-You + Talk to Expert ── */}
          <RecommendationsSection
            hasAccess={hasAccess} recommendations={recommendations} issues={issues}
            copy_assets={copy_assets} fastest_wins={fastest_wins} overall={overall}
            proj90={proj90} missedLeads={missedLeads} loss={loss} annLoss={annLoss}
            handleUnlock={handleUnlock} checkoutLoading={checkoutLoading}
          />

          {hasAccess && <DFYSection />}

          <div id="done-for-you">
            <div id="talk-to-expert" className="pdf-hide">
              <TalkToExpert placeId={placeId} email={data?.email || ""} phone={data?.phone_number || ""} className="mt-4" />
            </div>
          </div>

          {/* PDF Footer — only visible during export */}
          <div className="pdf-footer-bar" data-testid="pdf-footer">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 16, height: 16, borderRadius: 4, background: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: "#fff", fontWeight: 900, fontSize: 8 }}>R</span>
              </div>
              <span style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 700, fontSize: 11, color: "#64748B" }}>RankifyLocal</span>
              <span style={{ color: "#CBD5E1", margin: "0 4px" }}>&middot;</span>
              <span style={{ fontSize: 11, color: "#94A3B8", fontFamily: "'IBM Plex Sans',sans-serif" }}>audit.rankifylocal.com</span>
            </div>
            <p style={{ fontSize: 10, color: "#94A3B8", margin: 0, fontFamily: "'IBM Plex Sans',sans-serif" }}>
              Report generated {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} &middot; Confidential
            </p>
          </div>

          <BottomCTASection
            revenue_impact={revenue_impact} competitor_comparison={competitor_comparison}
            ai_visibility={ai_visibility} citation_status={citation_status}
            scores={scores} issues={issues} hasAccess={hasAccess}
            handleUnlock={handleUnlock} checkoutLoading={checkoutLoading}
            issueCards={issueCards}
          />

        </main>
      </div>

      {/* Mobile "Jump to Section" menu */}
      <div className="report-sidebar" style={{ display: "none" }}>
        {/* Placeholder — desktop sidebar shown via CSS, mobile hidden */}
      </div>
      <div style={{ display: "none" }} className="report-mobile-nav-wrapper">
        {/* Mobile nav rendered below via button */}
      </div>
      <button
        onClick={() => setMobileNavOpen(!mobileNavOpen)}
        data-testid="mobile-nav-toggle"
        style={{
          display: "none", position: "fixed", bottom: 52, left: 12, zIndex: 55,
          width: 40, height: 40, background: "#002FA7", color: "#fff",
          border: "none", cursor: "pointer", alignItems: "center", justifyContent: "center",
          boxShadow: "0 2px 12px rgba(0,47,167,0.3)",
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
      </button>
      {mobileNavOpen && (
        <div
          data-testid="mobile-nav-panel"
          style={{
            position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200,
            background: "#fff", borderTop: "1px solid #E2E8F0",
            boxShadow: "0 -8px 32px rgba(0,0,0,0.12)", padding: "12px 0",
            maxHeight: "60vh", overflowY: "auto",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 16px 10px", borderBottom: "1px solid #E2E8F0" }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#94A3B8", letterSpacing: ".15em", textTransform: "uppercase", margin: 0, fontFamily: "'IBM Plex Sans',sans-serif" }}>Sections</p>
            <button onClick={() => setMobileNavOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          {[...NAV_FREE, ...NAV_PAID].map(n => (
            <button
              key={n.id}
              onClick={() => { scrollTo(n.id); setMobileNavOpen(false); }}
              style={{
                width: "100%", display: "block", padding: "12px 20px", border: "none",
                textAlign: "left", cursor: "pointer", fontSize: 14, fontWeight: activeNav === n.id ? 600 : 400,
                color: activeNav === n.id ? "#002FA7" : "#475569",
                background: activeNav === n.id ? "#EFF6FF" : "transparent",
                fontFamily: "'IBM Plex Sans',sans-serif",
              }}
            >
              {n.label}
            </button>
          ))}
        </div>
      )}

      <style>{`
        @media(max-width:860px){
          [data-testid="mobile-nav-toggle"]{display:flex!important}
        }
      `}</style>

      {/* Sticky "Talk to Expert" bottom bar */}
      <div style={{
        position: "sticky", bottom: 0, background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
        borderTop: "1px solid #E2E8F0", padding: "8px 12px",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        zIndex: 50, flexWrap: "wrap",
      }} data-testid="sticky-expert-bar">
        <span style={{ fontSize: 13, fontWeight: 500, color: "#475569", fontFamily: "'IBM Plex Sans',sans-serif", whiteSpace: "nowrap" }}>Questions about your results?</span>
        <button
          onClick={() => document.getElementById("talk-to-expert")?.scrollIntoView({ behavior: "smooth" })}
          style={{
            background: "#002FA7", color: "#fff", border: "none",
            padding: "8px 16px", fontWeight: 600,
            cursor: "pointer", fontSize: 13, whiteSpace: "nowrap",
            fontFamily: "'IBM Plex Sans',sans-serif",
          }}
          data-testid="sticky-expert-btn"
        >
          Talk to an Expert — Free Call
        </button>
      </div>

      {/* Chat Q&A Widget */}
      <AuditChat placeId={placeId} businessName={business?.name} hasAccess={hasAccess} onUnlock={handleUnlock} />
    </div>
  );
}
